const STORAGE_KEY = 'odo:wq:search-history';
const MAX_ENTRIES = 4;
// Entries older than this are dropped on read — a week-old "recent" search is
// noise in the prompt, not a memory aid.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface SearchHistoryEntry {
    id: string;
    name: string;
    practice: string;
    profileThumbnailUrl: string | null;
    /** ISO timestamp of when the patient was selected. */
    selectedAt: string;
}

function isSearchHistoryEntry(value: unknown): value is SearchHistoryEntry {
    return (
        typeof value === 'object' &&
        value !== null &&
        typeof (value as Record<string, unknown>).id === 'string' &&
        typeof (value as Record<string, unknown>).name === 'string' &&
        typeof (value as Record<string, unknown>).practice === 'string' &&
        typeof (value as Record<string, unknown>).selectedAt === 'string' &&
        ((value as Record<string, unknown>).profileThumbnailUrl === null ||
            typeof (value as Record<string, unknown>).profileThumbnailUrl === 'string')
    );
}

function isFresh(entry: SearchHistoryEntry): boolean {
    const ts = new Date(entry.selectedAt).getTime();
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < MAX_AGE_MS;
}

export function readSearchHistory(): SearchHistoryEntry[] {
    try {
        const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isSearchHistoryEntry).filter(isFresh);
    } catch {
        return [];
    }
}

export function pushSearchHistory(entry: Omit<SearchHistoryEntry, 'selectedAt'>): void {
    try {
        const existing = readSearchHistory();
        // Dedup by id — re-selecting moves to front.
        const deduped = existing.filter((e) => e.id !== entry.id);
        const next = [{ ...entry, selectedAt: new Date().toISOString() }, ...deduped].slice(
            0,
            MAX_ENTRIES
        );
        globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // Storage unavailable or quota exceeded — silently no-op.
    }
}

export function removeSearchHistory(id: string): void {
    try {
        const existing = readSearchHistory();
        const next = existing.filter((e) => e.id !== id);
        globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
        // Storage unavailable — silently no-op.
    }
}
